import React, { useCallback } from 'react';
import cl from 'classnames';

import type {
  GlobalCompanyTabNanoID,
  GlobalCompanyTabRoleUID,
  GlobalCompanyTabUUID
} from '../../../globalCompanyTabsTypes';
import { TeamNanoID } from '../../../../teams/teamsTypes';

import { DELETE_GLOBAL_COMPANY_TAB_QUERY } from '../../../queries/deleteGlobalCompanyTab.query';

import { useDeleteGlobalCompanyTab } from '../../../hooks/useDeleteGlobalCompanyTab';

import { useThirdPersonView } from '../../../../../app/hooks/useThirdPersonView';

import { UpdateGlobalCompanyTabModalButton } from '../../modalButtons/UpdateGlobalCompanyTabModalButton';

import { ConfirmModalButton } from '../../../../../helpers/buttons/ConfirmModalButton';
import { NextPureLinkHelper } from '../../../../../helpers/links/NextPureLinkHelper';

import { GlobalCompanyTabsCache } from '../../../GlobalCompanyTabsCache';
import { words, stringsKeys } from '../../../../../locales/keys';

import { IconsEnum } from '../../../../../assets/icons/types';
import { TeamPath } from '../../../../teams/TeamPath';

interface GlobalCompanyTabsMenuDropdownItemProps {
  active: boolean;
  name: string;
  onDeleteActiveTab?: () => void;
  onSelectTab?: () => void;
  globalCompanyTabNanoId: GlobalCompanyTabNanoID;
  globalCompanyTabUuid: GlobalCompanyTabUUID;
  globalCompanyTabRoleIds: GlobalCompanyTabRoleUID[];
  companyNanoId: TeamNanoID;
  withUpdateButton: boolean;
  withDeleteButton: boolean;
}

function GlobalCompanyTabsMenuDropdownItem({
  active,
  name,
  onDeleteActiveTab,
  onSelectTab,
  globalCompanyTabNanoId,
  globalCompanyTabUuid,
  globalCompanyTabRoleIds,
  companyNanoId,
  withUpdateButton,
  withDeleteButton
}: GlobalCompanyTabsMenuDropdownItemProps) {
  const { isThirdPersonView } = useThirdPersonView();

  const tabHref = isThirdPersonView
    ? TeamPath.companyGlobalTab(companyNanoId, globalCompanyTabNanoId)
    : TeamPath.currentCompanyGlobalTab(globalCompanyTabNanoId);

  const {
    deleteGlobalCompanyTab,
    deleteGlobalCompanyTabLoading,
    deleteGlobalCompanyTabErrorMessage,
    deleteGlobalCompanyTabReset
  } = useDeleteGlobalCompanyTab({
    query: DELETE_GLOBAL_COMPANY_TAB_QUERY,
    cacheKeys: [GlobalCompanyTabsCache.indexCacheKey()]
  });

  const handleDeleteTab = useCallback<() => Promise<void>>(
    () =>
      deleteGlobalCompanyTab({ uid: globalCompanyTabUuid }).then(() => {
        if (active) {
          onDeleteActiveTab?.();
        }
      }),
    [active, deleteGlobalCompanyTab, globalCompanyTabUuid, onDeleteActiveTab]
  );

  return (
    <li
      className={cl(
        'bg-gray-500 duration-150 ease-in-out flex group items-center py-2 relative transition-colors -mx-4 pl-6 pr-4',
        active
          ? 'bg-opacity-25 hover:bg-opacity-30'
          : 'bg-opacity-0 hover:bg-opacity-20'
      )}
    >
      <NextPureLinkHelper
        className="focus:outline-none flex-1 px-2 text-sm truncate"
        href={tabHref}
        text={name}
        onClick={onSelectTab}
      />

      <div className="flex items-center space-x-1">
        {withUpdateButton && (
          <UpdateGlobalCompanyTabModalButton
            globalCompanyTabUuid={globalCompanyTabUuid}
            name={name}
            roleIds={globalCompanyTabRoleIds}
            icon={IconsEnum.PENCIL}
            disabled={deleteGlobalCompanyTabLoading}
            className="sm:absolute sm:opacity-0 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 focus:ring-offset-0 group-hover:relative group-hover:opacity-100 focus:relative focus:opacity-100 rounded-full z-10"
          />
        )}

        {withDeleteButton && (
          <ConfirmModalButton
            className="sm:absolute sm:opacity-0 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 focus:ring-offset-0 group-hover:relative group-hover:opacity-100 focus:relative focus:opacity-100 rounded-full z-10"
            errorMessage={deleteGlobalCompanyTabErrorMessage}
            i18nSubmitText={words.delete}
            i18nText={stringsKeys.areYouSureYouWantToDeleteTab}
            i18nTitle={words.delete}
            icon={IconsEnum.X}
            id={`delete-global-company-tab-modal-${globalCompanyTabNanoId}`}
            isLoading={deleteGlobalCompanyTabLoading}
            modalIcon={IconsEnum.EXCLAMATION}
            modalIconClassName="h-6 w-6 text-red-600"
            onClose={deleteGlobalCompanyTabReset}
            onSubmit={handleDeleteTab}
          />
        )}
      </div>
    </li>
  );
}

export default GlobalCompanyTabsMenuDropdownItem;
